import { EnvelopeSimple, Paperclip } from '@phosphor-icons/react'
import { useState } from 'react'
import { EMAIL_ATTACHMENT_URLS_QUERY } from 'app/components/contacts/inbox/graphql'
import { Button } from 'app/components/ui/button'
import { gqlQuery } from 'app/lib/graphqlClient'
import { notifyError } from 'app/services/notify.client'
import type { MessageJson } from 'app/types/entities'
import { logError } from 'lib/logging'

export function EmailMessageCard({ message }: { message: MessageJson }) {
  const [expanded, setExpanded] = useState(false)
  const outgoing = Boolean(message.sentByUserId || message.isAgent)
  const attachments = message.attachments ?? []

  const openAttachment = async (index: number) => {
    const popup = window.open(``, `_blank`)
    if (!popup) {
      notifyError(`Couldn't open this file. Try again.`)
      return
    }
    popup.opener = null
    const result = await gqlQuery(EMAIL_ATTACHMENT_URLS_QUERY, { id: message.id! }, { requestPolicy: `network-only` })
    const url = result.data?.emailAttachmentUrls?.[index]?.url
    if (!url) {
      popup.close()
      logError(`emailAttachmentUrls failed`, result.error?.message)
      notifyError(`Couldn't open this file. Try again.`)
      return
    }
    popup.location.href = url
  }

  return (
    <div className={`flex w-full ${outgoing ? `justify-end` : `justify-start`}`}>
      <div className="flex w-full max-w-3/4 flex-col gap-1.5 rounded-xl border border-default bg-surface px-3.5 py-2.5 text-body-sm shadow-elevation-1 max-md:max-w-11/12">
        <div className="flex items-center gap-1.5 text-label-xs text-subtle">
          <EnvelopeSimple className="h-3.5 w-3.5" />
          <span>Email to {message.to}</span>
        </div>
        <div className="font-semibold text-strong">{message.subject}</div>
        {expanded && message.htmlBody ? (
          <div
            className="text-default [&_a]:text-primary-600 [&_a]:underline [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5"
            // htmlBody is sanitized by the API on send to the allowlist in TRK-6054.
            dangerouslySetInnerHTML={{ __html: message.htmlBody }}
          />
        ) : (
          <div className="line-clamp-2 text-default">{message.text}</div>
        )}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {attachments.map((a, i) => (
              <Button
                key={`${a.filename}-${i}`}
                type="button"
                variant="white"
                size="xs"
                onClick={() => openAttachment(i)}
                className="max-w-full"
              >
                <Paperclip className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{a.filename}</span>
              </Button>
            ))}
          </div>
        )}
        {message.htmlBody && (
          <Button
            type="button"
            variant="link"
            size="inline"
            className="self-start"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? `Show less` : `Show full email`}
          </Button>
        )}
      </div>
    </div>
  )
}
